import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Calendar, MapPin, Users, Briefcase, Clock, TrendingUp, Award, Heart,
  ChevronDown, Bell, Mail, Sparkles, Coffee, Car, ArrowRight,
  Dumbbell, Utensils, HandHeart, Bus, ChefHat, Gift, Palmtree,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { db } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface JobPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'full-time' | 'part-time' | 'contract';
  experience: string;
  salary: string;
  description: string;
  job_description: string[];
  requirements: string[];
  benefits: string[];
  posted_date: string;
  is_active: boolean;
}

const Careers = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [funFactIndex, setFunFactIndex] = useState(0);

  useEffect(() => { loadPositions(); }, []);

  useEffect(() => {
    const i = setInterval(() => setFunFactIndex((p) => (p + 1) % 3), 5000);
    return () => clearInterval(i);
  }, []);

  const loadPositions = async () => {
    try {
      const data = await db.jobs.getActive();
      setPositions(data);
    } catch (error) {
      console.error('Error loading positions:', error);
      setPositions([]);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await db.jobNotifications.subscribe(email);
      if (result.success) {
        toast({ title: t('careers.empty.success'), description: t('careers.empty.success.description') });
        setEmail('');
      } else {
        toast({ title: t('careers.empty.already.subscribed'), description: 'You are already subscribed for job notifications.' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to subscribe. Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const departments = ['all', 'Výroba', 'Kvalita', 'Logistika', 'Administratíva', 'IT'];
  const jobTypes = ['all', 'full-time', 'part-time', 'contract'];

  const filteredPositions = positions.filter((pos) => {
    const mDep = selectedDepartment === 'all' || pos.department === selectedDepartment;
    const mType = selectedType === 'all' || pos.type === selectedType;
    return mDep && mType && pos.is_active;
  });

  const benefits = [
    { icon: TrendingUp, title: t('careers.benefits.growth.title'), description: t('careers.benefits.growth.description') },
    { icon: Award, title: t('careers.benefits.salary.title'), description: t('careers.benefits.salary.description') },
    { icon: Heart, title: t('careers.benefits.perks.title'), description: t('careers.benefits.perks.description') },
    { icon: Users, title: t('careers.benefits.team.title'), description: t('careers.benefits.team.description') },
  ];

  const funFacts = [
    { icon: Sparkles, text: t('careers.empty.fun.fact1') },
    { icon: Coffee, text: t('careers.empty.fun.fact2') },
    { icon: Car, text: t('careers.empty.fun.fact3') },
  ];

  const perks = [
    { icon: Dumbbell, title: t('careers.why.multisport.title'), description: t('careers.why.multisport.description') },
    { icon: Utensils, title: t('careers.why.cafeteria.title'), description: t('careers.why.cafeteria.description') },
    { icon: HandHeart, title: t('careers.why.massage.title'), description: t('careers.why.massage.description') },
    { icon: Bus, title: t('careers.why.transport.title'), description: t('careers.why.transport.description') },
    { icon: ChefHat, title: t('careers.why.canteen.title'), description: t('careers.why.canteen.description') },
    { icon: Gift, title: t('careers.why.bonus.title'), description: t('careers.why.bonus.description') },
    { icon: Palmtree, title: t('careers.why.vacation.title'), description: t('careers.why.vacation.description') },
  ];

  const jobTypeLabel = (type: string) =>
    type === 'full-time' ? t('careers.filter.type.fulltime')
    : type === 'part-time' ? t('careers.filter.type.parttime')
    : type === 'contract' ? t('careers.filter.type.contract')
    : t('careers.filter.type.all');

  return (
    <section id="careers" className="section-pad bg-background">
      <div className="container-tight">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <span className="eyebrow mb-6 block">{t('careers.badge')}</span>
            <h2 className="font-display font-bold text-primary" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1.02 }}>
              {t('careers.title')}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-6 lg:col-start-7 lede pt-2"
          >
            {t('careers.subtitle')}
          </motion.p>
        </div>

        {/* Benefit pillars */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[hsl(var(--hairline))] border border-[hsl(var(--hairline))] rounded-xl overflow-hidden mb-24"
        >
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="group bg-background p-8 md:p-10 transition-colors duration-300 hover:bg-muted/40"
            >
              <div className="flex items-center justify-between mb-10">
                <span className="section-num">0{i + 1}</span>
                <b.icon className="w-5 h-5 text-secondary" />
              </div>
              <h3 className="font-display text-xl font-semibold text-primary group-hover:text-secondary transition-colors mb-3">
                {b.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{b.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Why us — editorial split with sticky heading + perk rows */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left — sticky title */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <span className="eyebrow mb-6 block">{t('sub.whyArtifex')}</span>
                <h3
                  className="font-display font-semibold text-primary tracking-tight"
                  style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)', lineHeight: 1.05 }}
                >
                  {t('careers.why.title')}
                </h3>

                {/* CTA anchored to title */}
                <div className="mt-10 hidden lg:block">
                  <div className="hairline mb-8" />
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {t('careers.interested.description')}
                  </p>
                  <button
                    onClick={() =>
                      window.open('mailto:lukas.panis@artifex-systems.com?subject=Prihláška do tímu Artifex', '_blank')
                    }
                    className="group inline-flex items-center gap-2 font-display text-sm font-semibold text-primary hover:text-secondary transition-colors"
                  >
                    {t('careers.interested.button')}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>

            {/* Right — perks list, one per row with icon + number */}
            <div className="lg:col-span-8">
              <ul className="border-t border-[hsl(var(--hairline))]">
                {perks.map((p, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="group grid grid-cols-12 gap-4 md:gap-6 items-start py-6 md:py-7 border-b border-[hsl(var(--hairline))] transition-colors"
                  >
                    <div className="col-span-2 md:col-span-1 flex items-center">
                      <span className="font-display text-xs tabular-nums tracking-[0.22em] text-muted-foreground">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex items-center">
                      <span className="w-10 h-10 rounded-full border border-[hsl(var(--hairline))] group-hover:border-secondary group-hover:bg-secondary/5 flex items-center justify-center transition-all">
                        <p.icon className="w-4 h-4 text-primary group-hover:text-secondary transition-colors" />
                      </span>
                    </div>
                    <div className="col-span-8 md:col-span-4">
                      <h4 className="font-display font-semibold text-primary group-hover:text-secondary transition-colors">
                        {p.title}
                      </h4>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:pt-1">
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>

              {/* Mobile CTA */}
              <div className="mt-10 lg:hidden p-6 rounded-xl border border-[hsl(var(--hairline))] bg-muted/30">
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {t('careers.interested.description')}
                </p>
                <button
                  onClick={() =>
                    window.open('mailto:lukas.panis@artifex-systems.com?subject=Prihláška do tímu Artifex', '_blank')
                  }
                  className="btn-primary w-full"
                >
                  {t('careers.interested.button')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-10"
        >
          <div className="flex items-end justify-between mb-8">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-primary">
                {t('sub.openPositions')}
              </h3>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-xs font-display uppercase tracking-[0.18em] text-muted-foreground mb-3 block">
                {t('careers.filter.department')}
              </label>
              <div className="flex flex-wrap gap-2">
                {departments.map((d) => (
                  <button
                    key={d}
                    onClick={() => setSelectedDepartment(d)}
                    className={`px-4 py-2 rounded-full text-sm font-display font-medium transition-all ${
                      selectedDepartment === d
                        ? 'bg-primary text-primary-foreground border border-primary'
                        : 'bg-transparent text-primary border border-[hsl(var(--hairline))] hover:border-primary'
                    }`}
                  >
                    {d === 'all' ? t('careers.filter.department.all') : d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-display uppercase tracking-[0.18em] text-muted-foreground mb-3 block">
                {t('careers.filter.type')}
              </label>
              <div className="flex flex-wrap gap-2">
                {jobTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-4 py-2 rounded-full text-sm font-display font-medium transition-all ${
                      selectedType === type
                        ? 'bg-primary text-primary-foreground border border-primary'
                        : 'bg-transparent text-primary border border-[hsl(var(--hairline))] hover:border-primary'
                    }`}
                  >
                    {jobTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Positions */}
        <div className="border border-[hsl(var(--hairline))] rounded-xl overflow-hidden">
          {filteredPositions.map((position, i) => {
            const open = expandedId === position.id;
            return (
              <div key={position.id} className={`border-b border-[hsl(var(--hairline))] last:border-b-0 ${open ? 'bg-muted/30' : 'bg-background'}`}>
                <button
                  onClick={() => setExpandedId(open ? null : position.id)}
                  className="w-full p-6 md:p-8 grid grid-cols-12 gap-6 items-center text-left group hover:bg-muted/40 transition-colors"
                >
                  <span className="section-num col-span-1">{String(i + 1).padStart(2, '0')}</span>
                  <div className="col-span-12 md:col-span-5">
                    <h4 className="font-display text-xl md:text-2xl font-semibold text-primary group-hover:text-secondary transition-colors">
                      {position.title}
                    </h4>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{position.department}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{position.location}</span>
                    </div>
                  </div>
                  <div className="hidden md:block md:col-span-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full border border-[hsl(var(--hairline))] text-xs font-display font-medium text-primary">
                      {jobTypeLabel(position.type)}
                    </span>
                  </div>
                  <div className="hidden md:flex md:col-span-3 items-center gap-2 text-primary">
                    <TrendingUp className="w-4 h-4 text-secondary" />
                    <span className="font-display font-semibold">{position.salary}</span>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex justify-end">
                    <motion.div
                      animate={{ rotate: open ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-9 h-9 rounded-full border border-[hsl(var(--hairline))] group-hover:border-primary flex items-center justify-center transition-colors"
                    >
                      <ChevronDown className="w-4 h-4 text-primary" />
                    </motion.div>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 md:px-8 pb-8">
                        <div className="hairline mb-8" />
                        <p className="text-foreground/75 leading-relaxed mb-10 max-w-3xl">
                          {position.description}
                        </p>
                        <div className="grid md:grid-cols-2 gap-10 mb-10">
                          <div>
                            <h5 className="font-display text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                              {t('careers.job.description')}
                            </h5>
                            <ul className="space-y-2.5">
                              {(position.job_description || []).map((d, idx) => (
                                <li key={idx} className="flex items-start gap-3 text-sm text-foreground/80">
                                  <span className="text-secondary mt-1">▸</span>
                                  <span>{d}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="font-display text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                              — {t('careers.job.requirements')}
                            </h5>
                            <ul className="space-y-2.5">
                              {position.requirements.map((r, idx) => (
                                <li key={idx} className="flex items-start gap-3 text-sm text-foreground/80">
                                  <span className="text-secondary mt-1">▸</span>
                                  <span>{r}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-6 mb-8 pt-6 border-t border-[hsl(var(--hairline))]">
                          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                            <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{t('careers.job.experience')}: <strong className="text-primary">{position.experience}</strong></span>
                            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{t('careers.job.posted')}: <strong className="text-primary">{new Date(position.posted_date).toLocaleDateString('sk-SK')}</strong></span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`mailto:lukas.panis@artifex-systems.com?subject=Prihláška: ${position.title}`, '_blank');
                            }}
                            className="btn-primary"
                          >
                            <Mail className="w-4 h-4" /> {t('careers.job.apply')} <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {filteredPositions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 border border-[hsl(var(--hairline))] rounded-xl bg-muted/30"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[hsl(var(--hairline))] flex items-center justify-center bg-background">
              <Briefcase className="w-7 h-7 text-secondary" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-primary mb-3">
              {t('careers.empty.title')}
            </h3>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {t('careers.empty.description')}
            </p>

            <motion.div
              key={funFactIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-[hsl(var(--hairline))] bg-background mb-8"
            >
              {(() => {
                const Icon = funFacts[funFactIndex].icon;
                return <Icon className="w-4 h-4 text-secondary" />;
              })()}
              <span className="text-xs font-display uppercase tracking-[0.18em] text-secondary">
                {t('careers.fun.fact.label')}
              </span>
              <span className="text-sm text-foreground/80">{funFacts[funFactIndex].text}</span>
            </motion.div>

            <form onSubmit={handleSubscribe} className="max-w-md mx-auto flex gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder={t('careers.empty.email.placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-full border-[hsl(var(--hairline))] bg-background"
                  required
                />
              </div>
              <Button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? t('careers.submitting') : (<><Bell className="w-4 h-4" />{t('careers.empty.submit')}</>)}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-4">{t('careers.subscribe.disclaimer')}</p>
          </motion.div>
        )}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <Card className="border border-[hsl(var(--hairline))] rounded-xl shadow-none bg-muted/30">
            <CardContent className="p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-primary mb-2">
                  {t('careers.contact.title')}
                </h3>
                <p className="text-muted-foreground max-w-xl">{t('careers.contact.description')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => window.open('mailto:lukas.panis@artifex-systems.com', '_blank')} className="btn-primary">
                  <MapPin className="w-4 h-4" /> {t('careers.contact.hr')}
                </button>
                <button onClick={() => window.open('tel:+421123456789', '_blank')} className="btn-secondary">
                  {t('careers.contact.call')}
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Careers;
